import { useCallback, useEffect, useRef, useState, createContext, useMemo, useContext, Dispatch, SetStateAction } from "react";
import ReactFlow, {
  Node,
  addEdge,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Edge,
  Connection,
  Controls,
  useNodesState,
  useEdgesState,
  ReactFlowInstance,
  NodeTypes,
  XYPosition,
  OnSelectionChangeParams,
  MarkerType,
  MiniMap,
  useStore,
  getRectOfNodes,
  SelectionMode,
  NodeDragHandler,
  ReactFlowJsonObject,
  useKeyPress,
  Viewport,
  NodeChange,
  EdgeChange,
  applyEdgeChanges,
  applyNodeChanges,
  useViewport,
} from "reactflow";
import { getChatGPTOverarchingTopic, getChatGPTRelatedTopics, getChatGPTResponse, getTopics } from "../../../api/openai-api";

import "reactflow/dist/style.css";
import '@reactflow/node-resizer/dist/style.css';
import * as htmlToImage from 'html-to-image';
import './canvas-flow.scss';

// Components
import NodeToolkit from "../../components/node-toolkit/node-toolkit";
import SelectedTopicsToolbar from "../../components/selected-topics-toolbar/selected-topics-toolbar";
import TravellerConnectionLine from "../../edges/traveller-edge/traveller-connection";

// Nodes
import ChatNode from "../../nodes/response-node/response-node";
import { ChatNodeData, TypeChatNode } from '../../nodes/response-node/response-node.model';
import MemoNode from "../../nodes/memo-node/memo-node";
import { MemoNodeData, TypeMemoNode } from "../../nodes/memo-node/memo-node.model";
import { CreativeNode, ZoomState } from "../../nodes/node.model";
import TopicNode from "../../nodes/topic-node/topic-node";
import {
  TopicNodeData,
  TypeTopicNode,
} from "../../nodes/topic-node/topic-node.model";
import BrainstormNode from "../../nodes/brainstorm-node/brainstorm-node";
import GroupNode from "../../nodes/group-node/group-node";
import edgeTypes from "../../edges";
import {
  getNodePositionInsideParent,
  sortNodes,
} from "../../nodes/group-node/group-node.helper";

import { devFlags, projectTitle, uuid } from "../../utils";
import {
  Instance,
  InstanceMap,
  InstanceState,
  NodeEdgeList,
  semanticDiveIn,
  semanticDiveOut,
  semanticDiveTo,
  totalTransitionTime,
} from "../../triggers/semantic-dive/semantic-dive";
import SemanticRoute from "../../components/semantic-route/semantic-route";

import { useLocalStorage } from "../../hooks/useLocalStorage";
import { FlexNodeData } from "../../nodes/flex-node/flex-node.model";
import FlexNode from "../../nodes/flex-node/flex-node";
import { createTravellerEdge } from "../../edges/traveller-edge/traveller-edge.helper";
import { usePrevious } from "../../hooks/usePrevious";
import useAnimatedNodes from "../../hooks/useAnimatedNodes";
import useExpandCollapse from '../../hooks/useExpandCollapse';
import { duplicateNode, extractNodeTexts, getCenterOfNodes, predictSubtopics } from "../../nodes/node.helper";
import { clearSemanticCarry, getInstanceName, SemanticRouteItem, styleNodesAndEdges } from "../../triggers/semantic-dive/semantic-dive.helper";
import { notification } from "antd";
import React from "react";
import HierarchyNode from "../../nodes/hierarchy-node/hierarchy-node";
import { hideHierarchyViewTo, showHierarchyView } from "../../triggers/hierarchy-view/hierarchy-view";
import { HierarchyNodeData, TypeHierarchyNode } from "../../nodes/hierarchy-node/hierarchy-node.model";
import { createHierarchyNode } from "../../nodes/hierarchy-node/hierarchy-node.helper";
import LayerView from "../hierarchy-view/layer-view";
import { constructRoute } from "../../components/semantic-route/semantic-route.helper";

import { future_of_work, example } from "../../user-study/script";
import { FlowProps } from "../../App";
import { initialSensecapeNodes } from "./canvas-initial-elements";
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';

const nodeColor = (node: Node) => {
  switch (node.type) {
    case "brainstorm":
      // return '#4193F5';
      return "#6ede87";
    case "chat":
      return "#FF4500";
    case "concept":
      return "#0984e326";
    case "topic":
      return "#6865A5";
    case "subtopic":
      return "#6865A5";
    case "suptopic":
      return "#6865A5";
    case "memo":
      return "#FFFF00";
    case "hierarchy":
      return "#3c6792";
    default:
      return "#ddd";
  }
};

// const initialNodes: Node[] = []

// const initialNodes: Node[] = [
//   {
//     id: '1',
//     type: 'flex',
//     data: {
//       placeholder: 'Get answer from question: e.g., What / When / How / Why / Where ... ?',
//       state: {}
//     },
//     dragHandle: '.drag-handle',
//     position: { x: 250, y: 0 },
//   },
// ];

const initialEdges: Edge[] = [];

const proOptions = { account: "paid-pro", hideAttribution: true };

const panOnDrag = [1, 2];

const nodeTypes: NodeTypes = {
  brainstorm: BrainstormNode,
  chat: ChatNode,
  topic: TopicNode,
  memo: MemoNode,
  flex: FlexNode,
  group: GroupNode,
  hierarchy: HierarchyNode,
};

export const InstanceMapContext = createContext<{
  instanceMap: InstanceMap,
  setInstanceMap: Dispatch<SetStateAction<InstanceMap>>,
}>({ instanceMap: {}, setInstanceMap: () => {} });

const SensecapeFlow = (props: FlowProps) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  const [nodes, setNodes] = useState(initialSensecapeNodes(props.task));
  const [edges, setEdges] = useState(initialEdges);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Whenever a node is deleted, all children should not have that parentNode
      const removedNodeIds: string = changes
        .filter(change => change.type === 'remove')
        .map((change: any) => change.id)
        .join(','); // id do not have comma, so searching with includes() is just a hack

      let nodeIdsToRemove: { id: string }[] = [];

      setNodes((nodes) => applyNodeChanges(changes, nodes.map(node => {
        // Remove the parentNode if node is a child of a removed
        if (removedNodeIds && node.parentNode?.includes(removedNodeIds)) {
          const parent = reactFlowInstance?.getNode(node.parentNode);
          node.position.x += (parent?.position.x ?? 0) - (parent?.width ?? 0)/2;
          node.position.y += parent?.position.y ?? 0;
          node.parentNode = undefined;
        }
        return node;
      })));

      reactFlowInstance?.deleteElements({nodes: nodeIdsToRemove});
    },
    [reactFlowInstance, setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const connectingNodeId = useRef("");
  const [selectedTopics, setSelectedTopics] = useState<TypeTopicNode[]>([]);

  const [travellerMode, setTravellerMode] = useState(true);
  const [showingLayerView, setShowingLayerView] = useState(false);
  const [showingHierarchy, setShowingHierarchy] = useState(false);
  const [showingRecommendations, setShowingRecommendations] = useState(true);

  const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(showingHierarchy, nodes, edges);
  const { nodes: animatedNodes } = useAnimatedNodes(visibleNodes);

  const zoomSelector = (s: any) => s.transform[2];
  const zoom: number = useStore(zoomSelector);
  const prevZoom = usePrevious(zoom) ?? 0;
  const [zoomRange, setZoomRange] = useState({min: 0.3, max: 3});
  const [infiniteZoom, setInfiniteZoom] = useState(false);

  const [nodeMouseOver, setNodeMouseOver] = useState<Node | null>(null);

  const homeTopicNode: TypeTopicNode = {
    id: 'topic-home',
    type: 'topic',
    dragHandle: '.drag-handle',
    data: {
      parentId: '',
      originalId: 'topic-home',
      chatHistory: [],
      instanceState: InstanceState.CURRENT, // To temporarily disable dive out of home
      state: {
        topic: projectTitle,
      },
    } as TopicNodeData,
    hidden: true,
    position: { x: 0, y: 0 },
  };

  // Topic Node Id of the current instance
  const [currentTopicId, setCurrentTopicId] = useLocalStorage<string>(
    'currentTopicId',
    homeTopicNode.id
  );

  const homeInstance: Instance = {
    parentId: '',
    topicNode: homeTopicNode,
    jsonObject: {
      nodes: [homeTopicNode] as Node[],
      edges: [] as Edge[],
      viewport: {x: 0, y: 0, zoom: 1},
    },
    level: 0,
    canvasImageURL: '',
    position: { x: 0, y: 0 },
  };

  // Maps topicNodeId to Instance
  const [instanceMap, setInstanceMap] = useLocalStorage<InstanceMap>(
    'instanceMap',
    {
      [currentTopicId]: homeInstance
    }
  );

  const defaultRouteItem: SemanticRouteItem = {
    title: getInstanceName(instanceMap[currentTopicId]!),
    topicId: currentTopicId,
    level: 0,
  };

  const [semanticRoute, setSemanticRoute] = useLocalStorage<SemanticRouteItem[]>("semanticRoute", [defaultRouteItem]);

  const [loadingTopicPrediction, setLoadingTopicPrediction] = useState(false);
  const [predictedTopicName, setPredictedTopicName] = useLocalStorage<string>('predictedTopicName', '');

  // List of nodes and edges to carry into another semantic level
  const [semanticCarryList, setSemanticCarryList] = useState<NodeEdgeList>({
    nodes: [],
    edges: [],
  });

  // Ask ChatGPT for the topic from the nodes in the canvas
  useEffect(() => {
    if (!predictedTopicName && !instanceMap[currentTopicId]?.parentId) {
      const extractedTexts = extractNodeTexts(nodes);

      if (
        !loadingTopicPrediction &&
        extractedTexts.length >= 1 &&
        (!predictedTopicName || predictedTopicName === projectTitle)
      ) {
        getChatGPTOverarchingTopic(extractedTexts, setLoadingTopicPrediction).then(response => {
          setPredictedTopicName((!response || response === projectTitle) ? '' : response);
          instanceMap[currentTopicId].topicNode.data.state.topic = response;
          setInstanceMap(instanceMap);
          semanticRoute[0]!.title = response ?? semanticRoute[0]!.title;
          setSemanticRoute(semanticRoute);
        });
      }
    }
  }, [reactFlowInstance, nodes, instanceMap, semanticRoute, currentTopicId]);

  // Whether view is animating or not. Only one transition allowed at a time
  const [isAnimating, setIsAnimating] = useState(false);
  const altKeyPressed = useKeyPress('Alt');
  const escKeyPressed = useKeyPress('Escape');
  // const travellerPressed = useKeyPress('t');
  const layerViewPressed = useKeyPress('h');
  // const hierarchyPressed = useKeyPress('r'); // Disabled hierarchy view
  const selectAllPressed = useKeyPress(['Meta+a']); // TODO: Cmd + A to select all nodes on canvas

  // To copy and paste nodes for carrying over nodes
  const [copyStatus, setCopyStatus] = useState(false);
  const copyPressed = useKeyPress('Meta+c');
  const pastePressed = useKeyPress('Meta+v');

  const recommendationsPressed = useKeyPress('r');

  const [api, contextHolder] = notification.useNotification();
  const [mousePosition, setMousePosition] = useState({x: 0, y: 0});

  const openCarryOverStatus = (nodeCount: number) => {
    api.open({
      message: `${nodeCount} node${nodeCount > 1 ? 's' : ''} in clipboard`,
      style: {
        width: 280,
      },
      placement: 'bottom',
      duration: 0,
      onClose: () => {
        setCopyStatus(false)
        setSemanticCarryList({
          nodes: [],
          edges: [],
        })
        openRemoveCarryOver(nodeCount)
      },
      icon: (
        <CopyOutlined
          style={{
            color: '#108ee9',
          }}
        />
      ),
    });
  };

  const openRemoveCarryOver = (nodeCount: number) => {
    api.open({
      message: `Removed ${nodeCount} node${nodeCount > 1 ? 's' : ''} from clipboard`,
      style: {
        width: 350,
      },
      icon: (
        <DeleteOutlined
          style={{
            color: '#108ee9',
          }}
        />
      ),      
      placement: 'bottom',
      duration: 2,
    });
  };

  const openEmptyClipboard = () => {
    api.open({
      message: `Empty clipboard first`,
      style: {
        width: 350,
      },
      icon: (
        <DeleteOutlined
          style={{
            color: '#108ee9',
          }}
        />
      ),      
      placement: 'bottom',
      duration: 2,
    });
  };

  // Copy nodes
  useEffect(() => {
    if (reactFlowInstance && copyPressed && !copyStatus && !showingLayerView) {
      const selectedNodes = reactFlowInstance.getNodes().filter(node => node.selected);
      if (selectedNodes.length === 0) return;
      setSemanticCarryList({
        nodes: selectedNodes,
        edges: reactFlowInstance.getEdges().filter(edge => edge.selected),
      });
      setCopyStatus(true)
      openCarryOverStatus(selectedNodes.length);
    }
  }, [reactFlowInstance, semanticCarryList, copyPressed, copyStatus]);

  // Paste nodes
  useEffect(() => {
    if (reactFlowInstance && pastePressed && copyStatus && !showingLayerView) {
      if (semanticCarryList.nodes.length > 0) {
        const groupRect = getRectOfNodes(semanticCarryList.nodes);
        const viewport = reactFlowInstance.getViewport();
        const carriedNodes = semanticCarryList.nodes.map(node => {
          const duplicate = duplicateNode(node);
          const difference_x = (groupRect.x + (groupRect.width / 2)) - duplicate.position.x;
          const difference_y = (groupRect.y + (groupRect.height / 2)) - duplicate.position.y;
          duplicate.position.x = - ((viewport.x) * (1/viewport.zoom)) + ((mousePosition.x) * (1/viewport.zoom)) - (difference_x );
          duplicate.position.y = - ((viewport.y) * (1/viewport.zoom)) + ((mousePosition.y) * (1/viewport.zoom)) - (difference_y );
          return duplicate;
        });
        reactFlowInstance.addNodes(carriedNodes);

        reactFlowInstance.fitView({
          duration: 400,
          padding: 5,
          nodes: carriedNodes,
        });
      }
      // clearSemanticCarry(setSemanticCarryList);
      // api.destroy();
      // setCopyStatus(false);
    }
  }, [reactFlowInstance, semanticCarryList, pastePressed, mousePosition])

  // Clears semantic carry list when esc key pressed
  useEffect(() => {
    if (escKeyPressed) {
      if (semanticCarryList.nodes.length > 0) {
        api.destroy();
        setCopyStatus(false);
        clearSemanticCarry(setSemanticCarryList);
        openRemoveCarryOver(semanticCarryList.nodes.length);
      }
    }
  }, [escKeyPressed]);

  // On first load, recover nodes from localstorage
  useEffect(() => {
    if (reactFlowInstance) {
      reactFlowInstance.addNodes(homeTopicNode);
    }
    if (devFlags.disableLocalStorage) {
      return;
    }
    const recoveredInstanceMap = JSON.parse(
      localStorage.getItem("instanceMap") ?? ""
    );
    const currentInstance = recoveredInstanceMap[currentTopicId];
    if (currentInstance && reactFlowInstance) {
      reactFlowInstance.setNodes(currentInstance.jsonObject.nodes);
      reactFlowInstance.setEdges(currentInstance.jsonObject.edges);
    }
  }, [reactFlowInstance]);

  // useEffect(() => {
  //   if (travellerPressed) {
  //     toggleTravellerMode();
  //   }
  // }, [travellerPressed]);

  // useEffect(() => {
  //   if (hierarchyPressed) {
  //     toggleHierarchyView();
  //   }
  // }, [hierarchyPressed]);

  // Enable Layer View
  useEffect(() => {
    if (reactFlowInstance && layerViewPressed) {
      toggleLayerView();
    }
  }, [reactFlowInstance, layerViewPressed]);

  // Updates the current instance of reactflow
  useEffect(() => {
    if (devFlags.disableLocalStorage) {
      return;
    }
    const currInstance = instanceMap[currentTopicId];
    if (
      currInstance &&
      reactFlowInstance &&
      !showingHierarchy &&
      !nodes.find(node => node.type === 'hierarchy')
    ) {
      currInstance.jsonObject = {
        nodes: nodes,
        edges: edges,
        viewport: reactFlowInstance.getViewport(),
      };
      instanceMap[currentTopicId] = currInstance;
      localStorage.setItem("instanceMap", JSON.stringify(instanceMap));
      setInstanceMap(instanceMap);
    }
  }, [reactFlowInstance, currentTopicId, instanceMap, nodes, edges, showingHierarchy]);

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      // @ts-ignore
      params.markerEnd = {
        type: MarkerType.Arrow,
        width: 20,
        height: 20,
      }
      reactFlowInstance?.setEdges((els) => addEdge(params, els))
    },
    [reactFlowInstance]
  );

  const onConnectStart = useCallback((_: any, { nodeId }: any) => {
    connectingNodeId.current = nodeId;
  }, []);

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper?.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData("dragNodeType");

      // check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return;
      }

      const data = JSON.parse(event.dataTransfer.getData("dragNodeData"));

      if (reactFlowInstance && reactFlowBounds) {
        const position: XYPosition = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });
        // Type of node denoted in id
        const nodeId = `${type}-${uuid()}`;
        const newNode: CreativeNode = {
          id: nodeId,
          dragHandle: ".drag-handle",
          type,
          position,
          data,
        };
        reactFlowInstance.setNodes((nodes) => nodes.concat(newNode));
        if (data.parentId) {
          // Add traveller edge
          const newEdge = createTravellerEdge(data.parentId, newNode.id, false);
          reactFlowInstance.setEdges((edges) => edges.concat(newEdge));
        }
        setTimeout(() => {
          const currElement = document.querySelectorAll(`[data-id="${nodeId}"]`)[0];
          const inputElement = currElement.getElementsByClassName('text-input')[0] as HTMLInputElement;
          inputElement?.focus();
        }, 100);
      }
    },
    [reactFlowInstance, travellerMode]
  );

  const onNodeDrag = useCallback(
    (_: any, node: Node) => {
      if (node.type !== "node" && !node.parentNode) {
        return;
      }

      if (reactFlowInstance) {
        const intersections = reactFlowInstance
          .getIntersectingNodes(node)
          .filter((n) => n.type === "group");
        const groupClassName =
          intersections.length && node.parentNode !== intersections[0]?.id
            ? "active"
            : "";

        reactFlowInstance.setNodes((nds) => {
          return nds.map((n) => {
            if (n.type === "group") {
              return {
                ...n,
                className: groupClassName,
              };
            } else if (n.id === node.id) {
              return {
                ...n,
                position: node.position,
              };
            }

            return { ...n };
          });
        });
      }
    },
    [reactFlowInstance]
  );

  const onNodeDragStop = useCallback(
    (_: any, node: Node) => {
      if (node.type !== "node") {
        return;
      }

      if (reactFlowInstance) {
        const intersections = reactFlowInstance
          .getIntersectingNodes(node)
          .filter((n) => n.type === "group");
        const groupNode = intersections[0];

        // when there is an intersection on drag stop, we want to attach the node to its new parent
        if (intersections.length && node.parentNode !== groupNode?.id) {
          const nextNodes: Node[] = reactFlowInstance
            .getNodes()
            .filter((node): node is Node => !!node)
            .map((n) => {
              if (n.id === groupNode.id) {
                return {
                  ...n,
                  className: "",
                };
              } else if (n.id === node.id) {
                const position = getNodePositionInsideParent(n, groupNode);
                if (position) {
                  return {
                    ...n,
                    position,
                    parentNode: groupNode.id,
                    extent: "parent" as "parent",
                  };
                }
              }

              return n;
            })
            .sort(sortNodes);

          reactFlowInstance.setNodes(nextNodes);
        }
      }
    },
    [reactFlowInstance]
  );

  const onSelectionChange = useCallback(
    (params: OnSelectionChangeParams) => {
      const selectedTopicNodes: TypeTopicNode[] = params.nodes.filter(
        (node) => node.type === "topic" && node.selected && !node.parentNode
      );

      setSelectedTopics(selectedTopicNodes);
      return;
    },
    [reactFlowInstance]
  );

  const onNodeClick = useCallback((e: any) => {
    switch (e.detail) {
      case 1:
        return;
      case 2:
        if ( // Naviate to canvas from Hierarchy View
          nodeMouseOver &&
          nodeMouseOver.type === 'hierarchy' &&
          reactFlowInstance
        ) {
          setShowingHierarchy(false);
          hideHierarchyViewTo(
            (nodeMouseOver as TypeHierarchyNode).data.topicNode.id,
            instanceMap,
            [currentTopicId, setCurrentTopicId],
            [semanticRoute, setSemanticRoute],
            [semanticCarryList, setSemanticCarryList],
            reactFlowInstance
          );
        } else if ( // Semantic Zoom by Double Click
          nodeMouseOver &&
          nodeMouseOver.type === 'topic' &&
          reactFlowInstance
        ) {
          if (nodeMouseOver.id !== currentTopicId) {
            saveCanvasInstance(currentTopicId);
            semanticDiveIn(
              nodeMouseOver,
              [infiniteZoom, setInfiniteZoom],
              [instanceMap, setInstanceMap],
              [currentTopicId, setCurrentTopicId],
              [semanticRoute, setSemanticRoute],
              [semanticCarryList, setSemanticCarryList],
              reactFlowInstance
            );
            // notifySemanticDive('⬇️ Dive in');
          } else {
            saveCanvasInstance(currentTopicId);
            semanticDiveOut(
              [predictedTopicName, setPredictedTopicName],
              [infiniteZoom, setInfiniteZoom],
              [instanceMap, setInstanceMap],
              [currentTopicId, setCurrentTopicId],
              [semanticRoute, setSemanticRoute],
              [semanticCarryList, setSemanticCarryList],
              reactFlowInstance
            );
            // notifySemanticDive('⬆️ Dive out');
          }
        }
        break;
    }
  },
  [reactFlowInstance, nodeMouseOver, currentTopicId, instanceMap, semanticRoute, semanticCarryList, predictedTopicName]);

  // add flex / topic / memo node when user double clicks on canvas
  const onPaneClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      if (reactFlowInstance) {
        const position: XYPosition = reactFlowInstance!.project({
          x: event.clientX,
          y: event.clientY,
        });

        if (event.detail === 1) {
          return;
        } else if ((event.altKey || event.metaKey || event.shiftKey) && event.detail === 2) {
          // alt or command or shift + double click to create memo node
          const data: MemoNodeData = {
            state: {},
          };

          const nodeId = `flex-${uuid()}`;
          reactFlowInstance.addNodes([
            {
              id: nodeId,
              type: 'memo',
              dragHandle: '.drag-handle',
              position,
              data,
            },
          ]);
      } else if (event.detail === 2) {
          if (showingHierarchy) {
            // Hierarchy view, doule click to create new hierarchy
            createHierarchyNode(
              [instanceMap, setInstanceMap],
              reactFlowInstance
            );
          } else {

            const nodeId = `flex-${uuid()}`;

            if (devFlags.disableFlexNode) { // add topic node

              let data = {
                parentId: '',
                originalId: nodeId,
                chatHistory: [],
                instanceState: InstanceState.NONE,
                state: {
                  topic: '',
                  hasTopic: false,
                  hasFourHandles: true,
                },
              } as TopicNodeData;
    
              const newNode: CreativeNode = {
                id: nodeId,
                dragHandle: ".drag-handle",
                type: 'topic',
                position,
                data: data as any,
              };
    
              reactFlowInstance.setNodes((nodes) =>
                nodes.concat(
                  newNode
                ));

                setTimeout(() => {
                  const currElement = document.querySelectorAll(`[data-id="${nodeId}"]`)[0];
                  const inputElement = currElement.getElementsByClassName('text-input')[0] as HTMLInputElement;
                  inputElement.focus();
                }, 100);   

            } else { // add flex node
              // Canvas, view, double click to create flex node
              const data: FlexNodeData = {
                // We want chat node to have no response yet, since the user will ask for a response
                placeholder: 'Ask a question: e.g., What is ...?',
                state: {},
              };
              
              reactFlowInstance.addNodes([
                {
                  id: nodeId,
                  type: 'flex',
                  dragHandle: '.drag-handle',
                  position,
                  data,
                },
              ]);
              setTimeout(() => {
                const currElement = document.querySelectorAll(`[data-id="${nodeId}"]`)[0];
                const inputElement = currElement.getElementsByClassName('text-input')[0] as HTMLInputElement;
                inputElement.focus();
                const buttonElements = currElement.getElementsByClassName('flex-node-button');
                for (let i = 0; i < 4; i++) {
                  buttonElements[i].addEventListener('mouseover', () => {
                  })
                }
              }, 100);            
            }
          }
        }
      }
    },
    [reactFlowInstance, instanceMap, semanticCarryList]
  );

  /**
   * Zoom scroll
   * Zoom out: show hierarchy
   * Zoom in: Dive in
   */
  useEffect(() => {
    if (reactFlowInstance && !isAnimating && Math.abs(zoom-prevZoom) < 1) {
      if (zoom > prevZoom && zoom === zoomRange.max && nodeMouseOver) {
        setIsAnimating(true);
        saveCanvasInstance(currentTopicId);
        semanticDiveIn(
          nodeMouseOver,
          [infiniteZoom, setInfiniteZoom],
          [instanceMap, setInstanceMap],
          [currentTopicId, setCurrentTopicId],
          [semanticRoute, setSemanticRoute],
          [semanticCarryList, setSemanticCarryList],
          reactFlowInstance
        );
        setTimeout(() => setIsAnimating(false), 1.5*totalTransitionTime);
      } else if (zoom < prevZoom && zoom === zoomRange.min) {
        toggleLayerView();
      }
    }
  }, [reactFlowInstance, isAnimating, zoom, prevZoom, infiniteZoom, nodeMouseOver,
    currentTopicId, instanceMap, semanticRoute, semanticCarryList, predictedTopicName])

  /**
   * Saves the current viewport of the current canvas
   */
  const saveCanvasInstance = useCallback(async (currentId: string, showLayerView?: boolean) => {
    const canvasStateElement = reactFlowWrapper.current;
    if (reactFlowInstance && canvasStateElement) {
      instanceMap[currentId].jsonObject = reactFlowInstance.toObject();
      const dataURL = await htmlToImage.toPng(canvasStateElement);
      if (showLayerView) {
        setShowingLayerView(!showingLayerView);
      }
      instanceMap[currentId].canvasImageURL = dataURL;
      setInstanceMap(instanceMap);
    }
  }, [reactFlowInstance, reactFlowWrapper, showingLayerView, instanceMap]);

  const enterCanvas = useCallback((selectedTopicId: string) => {
    if (reactFlowInstance) {
      setShowingLayerView(false);
      const instance = instanceMap[selectedTopicId];
      if (currentTopicId !== selectedTopicId) {
        reactFlowInstance.setNodes(instance.jsonObject.nodes);
        reactFlowInstance.setEdges(instance.jsonObject.edges);
        reactFlowInstance.setViewport(instance.jsonObject.viewport);
        styleNodesAndEdges(reactFlowInstance, [], {
          transition: 'none',
          opacity: 1,
        });
        setCurrentTopicId(selectedTopicId);
        setSemanticRoute(constructRoute(instance, instanceMap));
      }
    }
  }, [reactFlowInstance, currentTopicId, instanceMap]);

  /**
   * Enables or disables the layer view
   */
  const toggleLayerView = useCallback(async () => {
    if (!showingLayerView) {
      // Hides all toolkit elements
      document.querySelector('div.minimap')?.setAttribute('hidden', 'true');
      await saveCanvasInstance(currentTopicId, true);
      setShowingLayerView(true);
    } else {
      // When hiding layer view, run this code.
      // const instances = Object.values(instanceMap);
      // const minLeft = Math.min(...instances.map(instance => instance.position.x));
      // const maxLeft = Math.max(...instances.map(instance => instance.position.x));
      // const currentLayerElement = Array.from(document.getElementsByClassName('clicked-layer')) as HTMLElement[];
      // console.log(currentLayerElement);
      // currentLayerElement[0].classList.add('clicked-layer');
      // currentLayerElement[0].style.translate = `${window.scrollX-(maxLeft-minLeft)/2}px ${window.scrollY}px`;
      // setTimeout(() => {
      //   hideLayerView(currentTopicId);
      // }, 500);
    }
  }, [showingLayerView, currentTopicId]);

  /**
   * Toggles visibility of traveller edges to track progress
   */
  const toggleTravellerMode = useCallback(() => {
    setTravellerMode(!travellerMode);
    // DISABLED TRAVELLER MODE
    // if (reactFlowInstance) {
    //   reactFlowInstance.setEdges((edges) =>
    //     edges.map((edge) => {
    //       // if edge is traveller, toggle hidden
    //       if (edge.id.includes("edge-travel")) {
    //         edge.hidden = travellerMode;
    //       }
    //       return edge;
    //     }));
    //   }
    },
    [reactFlowInstance, travellerMode]
  ) 

  /**
   * Toggles hierarchy view
   */
  const toggleHierarchyView = useCallback(() => {
    if (reactFlowInstance) {
      if (!showingHierarchy) {
        setShowingHierarchy(true);
        showHierarchyView(
          predictedTopicName,
          currentTopicId,
          [instanceMap, setInstanceMap],
          reactFlowInstance
        );
      } else {
        setShowingHierarchy(false);
        hideHierarchyViewTo(
          currentTopicId,
          instanceMap,
          [currentTopicId, setCurrentTopicId],
          [semanticRoute, setSemanticRoute],
          [semanticCarryList, setSemanticCarryList],
          reactFlowInstance
        );
      }
    }
  },
  [showingHierarchy, predictedTopicName, currentTopicId, instanceMap, reactFlowInstance]);

  // Semantic Dive to 
  const semanticDiveToInstance = useCallback((nextTopicId: string) => {
    if (reactFlowInstance) {
      saveCanvasInstance(currentTopicId);
      semanticDiveTo(
        nextTopicId,
        [infiniteZoom, setInfiniteZoom],
        [instanceMap, setInstanceMap],
        [currentTopicId, setCurrentTopicId],
        [semanticRoute, setSemanticRoute],
        [semanticCarryList, setSemanticCarryList],
        reactFlowInstance,
      );
    }
  }, [reactFlowInstance, infiniteZoom, instanceMap, currentTopicId, semanticRoute, semanticCarryList])

  /**
   * Generates subtopics around the selection of nodes
   */
  const generateNewSubtopics = useCallback(
    async (selectedNodeIds: string[]) => {
      if (reactFlowInstance) {
        // get all contents of selected nodes
        const selectedNodes = selectedNodeIds
          .map(nodeId => reactFlowInstance.getNode(nodeId))
          .filter((node): node is Node => !!node);

        const nodeTexts = extractNodeTexts(selectedNodes);
        await predictSubtopics(nodeTexts, selectedNodeIds, reactFlowInstance);
      }
    },
    [reactFlowInstance]
  );

  const resetCanvas = useCallback(() => {
    if (reactFlowInstance) {
      localStorage.clear();
      if (showingHierarchy) {
        hideHierarchyViewTo(
          currentTopicId,
          instanceMap,
          [currentTopicId, setCurrentTopicId],
          [semanticRoute, setSemanticRoute],
          [semanticCarryList, setSemanticCarryList],
          reactFlowInstance
        );
        setShowingHierarchy(false);
      }
      setCurrentTopicId(homeTopicNode.id);
      setInstanceMap(    {
        [homeTopicNode.id]: {
          name: projectTitle,
          parentId: "",
          topicNode: homeTopicNode,
          jsonObject: {
            nodes: [] as Node[],
            edges: [] as Edge[],
            viewport: {x: 0, y: 0, zoom: 1},
          },
          level: 0,
          canvasImageURL: '',
          position: { x: 0, y: 0 },
        } as Instance,
      })
      setSemanticRoute([defaultRouteItem]);
      reactFlowInstance.setNodes([]);
      reactFlowInstance.setEdges([]);
      reactFlowInstance.zoomTo(1, {
        duration: 400,
      });
      reactFlowInstance.fitView();
    }
  }, [reactFlowInstance, showingHierarchy, currentTopicId, instanceMap, semanticRoute]);

  return (
      <InstanceMapContext.Provider value={{ instanceMap, setInstanceMap }}>
        {contextHolder}
        <div className="explore-flow">
          <div id="reactflow-wrapper" className="reactflow-wrapper" ref={reactFlowWrapper}>
            <ReactFlow
              id='reactFlowInstance'
              className={'flow'}
              proOptions={proOptions}
              nodes={showingHierarchy ? animatedNodes : nodes}
              edges={showingHierarchy ? visibleEdges : edges}
              fitView
              zoomOnPinch={!isAnimating}
              zoomOnScroll={!isAnimating}
              preventScrolling={!isAnimating}
              selectionOnDrag
              panOnDrag={panOnDrag}
              panOnScroll
              zoomOnDoubleClick={false}
              selectionMode={SelectionMode.Partial}
              multiSelectionKeyCode={'Shift'}
              nodeOrigin={[0.5, 0]}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              onConnect={onConnect}
              connectionLineComponent={TravellerConnectionLine}
              // fitViewOptions={fitViewOptions}
              onInit={setReactFlowInstance}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodeDrag={onNodeDrag}
              onNodeDragStop={onNodeDragStop}
              onNodeMouseEnter={(_, node) => setNodeMouseOver(node)}
              onNodeMouseLeave={() => setNodeMouseOver(null)}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onConnectStart={onConnectStart}
              // onConnectEnd={onConnectEnd} // if enabled, it generates sup- or sub- topics when user drags mouse out from handle
              onSelectionChange={onSelectionChange}
              onPaneClick={onPaneClick}
              onMouseMove={(e) => setMousePosition({x: e.clientX, y: e.clientY})}
              onNodesDelete={(event) => console.log(event)}
              maxZoom={infiniteZoom ? Infinity : zoomRange.max}
              minZoom={infiniteZoom ? -Infinity : zoomRange.min}
              elevateNodesOnSelect
            >
              <Controls className="sensecape-flow-controls" />
              <Background />
              {
                !showingLayerView ?
                <MiniMap
                  id='minimap'
                  className='minimap'
                  hidden={showingLayerView}
                  nodeColor={nodeColor}
                  nodeStrokeWidth={3}
                  zoomable
                  pannable
                /> :
                <></>
              }
            </ReactFlow>
          </div>
            {
              !showingLayerView ?  
              <div id='canvas-toolkit'>
                <div id='semantic-carry-box'></div>
                <SemanticRoute
                  currentTopicId={currentTopicId}
                  route={semanticRoute}
                  semanticJumpTo={semanticDiveToInstance}
                />
              </div> :
              <></>
            }
            <SelectedTopicsToolbar
              recommendSubtopics={generateNewSubtopics}
            />

        </div>
        {
          showingLayerView ?
          <LayerView
            currentTopicId={currentTopicId}
            hideLayerView={enterCanvas}
            map={instanceMap}
          /> :
          <></>
        }
        <NodeToolkit
          resetCanvas={resetCanvas}
          travellerMode={travellerMode}
          toggleTravellerMode={toggleTravellerMode}
          showingHierarchy={showingHierarchy}
          toggleHierarchyView={toggleHierarchyView}
          showingLayerView={showingLayerView}
          toggleLayerView={toggleLayerView}
        />
      </InstanceMapContext.Provider>

  );
};

export default SensecapeFlow;
